import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  Menu,
  Package,
  Truck,
  Users,
  Pencil,
  Plus,
  Trash2,
  Tag,
  Search,
} from 'lucide-react';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import QRCode from 'qrcode';
import Sidebar from '@/components/common/Sidebar';
import TrialBanner from '@/components/common/TrialBanner';
import ConfirmModal from '@/components/common/ConfirmModal';
import { useUIStore } from '@/store/uiStore';
import { useAuthStore } from '@/store/authStore';
import { isAdminOrManager, isCashierLevel, isCompanyAdmin } from '@/types';
import type { CustomerItemPrice, POSCustomer, PosSettings } from '@/types';
import { getApiErrorMessage } from '@/utils/apiError';
import { authApi, loyaltyApi, posApi, salesHubApi, storefrontAdminApi } from '@/services/api';
import { formatMoney, formatDate as formatCalendarDate } from '@/utils/currency';
import { isUk } from '@/data/locales';
import PartyLedgerModal from '@/components/PartyLedgerModal';
import SaleInvoiceLedgerModal from '@/components/customers/SaleInvoiceLedgerModal';
import CustomerQuotationsModal from '@/components/customers/CustomerQuotationsModal';
import AddCustomerPaymentModal from '@/components/customers/AddCustomerPaymentModal';
import CustomerFormModal from '@/components/customers/CustomerFormModal';
import CustomerFulfillmentTab from '@/components/customers/dashboard/CustomerFulfillmentTab';
import CustomerDetailHero from '@/components/customers/dashboard/CustomerDetailHero';
import CustomerKpiCards from '@/components/customers/dashboard/CustomerKpiCards';
import CustomerMidCards from '@/components/customers/dashboard/CustomerMidCards';
import CustomerOverviewPanel from '@/components/customers/dashboard/CustomerOverviewPanel';
import LoyaltyCardModal from '@/components/loyalty/LoyaltyCardModal';
import Receipt from '@/components/pos/Receipt';
import { createCustomerRouted, isLocalFirstMode } from '@/offline';
import { resolveFileUrl } from '@/utils/fileUrl';

type TabKey = 'overview' | 'invoices' | 'quotes' | 'prices' | 'collection' | 'delivery' | 'ledger';

const TAB_KEYS: TabKey[] = [
  'overview',
  'invoices',
  'quotes',
  'prices',
  'collection',
  'delivery',
  'ledger',
];

export default function CustomerDetailPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { customerId: rawId } = useParams<{ customerId: string }>();
  const customerId = rawId ? decodeURIComponent(rawId) : '';
  const [searchParams, setSearchParams] = useSearchParams();
  const { toggleSidebar } = useUIStore();
  const { role } = useAuthStore();
  const isAdmin = isCompanyAdmin(role);
  const canViewVoided = isAdminOrManager(role);
  const qc = useQueryClient();

  const [showDeleteCustomerConfirm, setShowDeleteCustomerConfirm] = useState(false);
  const [deleteCustomerPending, setDeleteCustomerPending] = useState(false);
  const [deleteCustomerError, setDeleteCustomerError] = useState<string | null>(null);

  const rawTab = searchParams.get('tab') || 'overview';
  // Legacy deep-links
  const normalizedTab = rawTab === 'fulfillment' ? 'delivery' : rawTab;
  const tabParam = normalizedTab as TabKey;
  const activeTab: TabKey = TAB_KEYS.includes(tabParam) ? tabParam : 'overview';

  const [showInvoiceLedger, setShowInvoiceLedger] = useState(false);
  const [showQuotations, setShowQuotations] = useState(false);
  const [showAddPayment, setShowAddPayment] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editModalTab, setEditModalTab] = useState<'basic' | 'prices'>('basic');
  const [editModalPriceIndex, setEditModalPriceIndex] = useState<number | null>(null);
  const [deletePriceTarget, setDeletePriceTarget] = useState<{
    item: string;
    item_name?: string;
    variant?: string;
    index: number;
  } | null>(null);
  const [deletePricePending, setDeletePricePending] = useState(false);
  const [priceSearchQuery, setPriceSearchQuery] = useState('');
  const [receiptInvoiceName, setReceiptInvoiceName] = useState<string | null>(null);
  const [invoiceStatusFilter, setInvoiceStatusFilter] = useState<string>('All');
  const [storeLoginEmail, setStoreLoginEmail] = useState('');
  const [storeLoginPassword, setStoreLoginPassword] = useState('');
  const [storeLoginBusy, setStoreLoginBusy] = useState(false);
  const [adjustPts, setAdjustPts] = useState('');
  const [adjustReason, setAdjustReason] = useState('');
  const [adjustBusy, setAdjustBusy] = useState(false);
  const [enrolMarketing, setEnrolMarketing] = useState(false);
  const [enrolBusy, setEnrolBusy] = useState(false);
  const [storeLoginOpen, setStoreLoginOpen] = useState(false);
  const [loyaltyQrDataUrl, setLoyaltyQrDataUrl] = useState<string | null>(null);
  const [showLoyaltyQr, setShowLoyaltyQr] = useState(false);
  const [headerMoreOpen, setHeaderMoreOpen] = useState(false);

  const handleDeleteCustomerConfirm = async () => {
    if (!customer) return;
    setDeleteCustomerPending(true);
    try {
      await posApi.deleteCustomer(customer.name);
      toast.success(t('customers.deletedSuccess', 'Customer deleted successfully'));
      qc.invalidateQueries({ queryKey: ['customers'] });
      qc.invalidateQueries({ queryKey: ['posCustomers'] });
      qc.invalidateQueries({ queryKey: ['posCustomersSearch'] });
      qc.invalidateQueries({ queryKey: ['posCustomerSelect'] });
      navigate('/customers');
    } catch (err: any) {
      const errMsg = getApiErrorMessage(err, 'Failed to delete customer');
      setShowDeleteCustomerConfirm(false);
      setDeleteCustomerError(errMsg);
    } finally {
      setDeleteCustomerPending(false);
    }
  };

  const openInvoiceReceipt = (invoiceName: string) => {
    if (!invoiceName) return;
    setReceiptInvoiceName(invoiceName);
  };

  const setTab = (tab: TabKey) => {
    const next = new URLSearchParams(searchParams);
    if (tab === 'overview') next.delete('tab');
    else next.set('tab', tab);
    setSearchParams(next, { replace: true });
  };

  const offline = isLocalFirstMode();

  const { data, isLoading, isError } = useQuery({
    queryKey: ['customer-dashboard', customerId],
    enabled: Boolean(customerId) && !offline,
    queryFn: async () => {
      const res = await posApi.getCustomerDashboard(customerId);
      return res.data.message;
    },
  });

  const { data: posSettings } = useQuery({
    queryKey: ['posSettings'],
    queryFn: async () => {
      const res = await posApi.getPosSettings();
      return res.data.message as PosSettings;
    },
  });

  const { data: profile } = useQuery({
    queryKey: ['profile'],
    queryFn: async () => (await authApi.getCompanyProfile()).data.message,
  });

  const { data: loyaltyInfo, refetch: refetchLoyalty } = useQuery({
    queryKey: ['customer-loyalty', customerId],
    enabled: Boolean(customerId) && !offline,
    queryFn: async () => {
      const res = await loyaltyApi.getCustomerBalance(customerId);
      return res.data.message as {
        balance?: number;
        value?: number;
        enrolled?: boolean;
        lifetime_points_earned?: number;
        tier?: { tier_name?: string; min_points?: number; badge_color?: string } | null;
        program_active?: boolean;
        qr?: string;
        barcode?: string;
        shop_name?: string;
        program_name?: string;
        card_design?: string;
        card_colour?: string;
        card_logo?: string;
        card_tagline?: string;
        card_show_points?: boolean;
        card_show_value?: boolean;
        card_show_tier?: boolean;
      };
    },
  });

  useEffect(() => {
    let cancelled = false;
    // Prefer customer ID so POS scan-to-select works; fall back to legacy SDLY token
    const payload =
      (loyaltyInfo?.barcode || customerId || loyaltyInfo?.qr || '').trim();
    if (!loyaltyInfo?.enrolled || !payload) {
      setLoyaltyQrDataUrl(null);
      return;
    }
    void QRCode.toDataURL(payload, { width: 220, margin: 1, errorCorrectionLevel: 'M' })
      .then((url) => {
        if (!cancelled) setLoyaltyQrDataUrl(url);
      })
      .catch(() => {
        if (!cancelled) setLoyaltyQrDataUrl(null);
      });
    return () => {
      cancelled = true;
    };
  }, [loyaltyInfo?.enrolled, loyaltyInfo?.qr, loyaltyInfo?.barcode, customerId]);

  const { data: invoices = [], isLoading: invoicesLoading } = useQuery({
    queryKey: ['customer-invoices-tab', customerId, invoiceStatusFilter],
    enabled: Boolean(customerId) && activeTab === 'invoices' && !offline,
    queryFn: async () => {
      const status =
        invoiceStatusFilter === 'All'
          ? undefined
          : invoiceStatusFilter === 'Unpaid'
            ? 'Unpaid'
            : invoiceStatusFilter === 'Paid'
              ? 'Paid'
              : invoiceStatusFilter === 'Returned'
                ? 'Returned'
                : invoiceStatusFilter === 'Voided'
                  ? 'Cancelled'
                  : undefined;
      const res = await posApi.getCustomerInvoices({
        customer: customerId,
        status,
      });
      let list = (res.data.message || []) as any[];
      if (invoiceStatusFilter === 'Partial') {
        list = list.filter((i) => i.status === 'Partially Paid');
      }
      return list;
    },
  });

  const { data: quotesData, isLoading: quotesLoading } = useQuery({
    queryKey: ['customer-quotes-tab', customerId],
    enabled: Boolean(customerId) && activeTab === 'quotes' && !offline,
    queryFn: async () => {
      const res = await salesHubApi.getList({
        tab: 'quotes',
        customer: customerId,
        status: 'Active',
        page: 1,
        limit: 50,
      });
      return res.data.message as { items?: any[]; total?: number };
    },
  });

  const customer = data?.customer;
  const shopIsUk = isUk(profile?.country || customer?.country);

  const storeLoginQ = useQuery({
    queryKey: ['posCustomerStoreLogin', customerId],
    enabled: Boolean(customerId) && !offline && isAdmin,
    queryFn: async () =>
      (await storefrontAdminApi.getPosCustomerStoreLogin(customerId)).data.message as Record<string, any>,
  });

  useEffect(() => {
    const email =
      storeLoginQ.data?.email || storeLoginQ.data?.suggested_email || customer?.email || '';
    if (email && !storeLoginEmail) setStoreLoginEmail(String(email));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeLoginQ.data, customer?.email]);

  const saveStoreLogin = async () => {
    if (!customer) return;
    setStoreLoginBusy(true);
    try {
      await storefrontAdminApi.setPosCustomerStoreLogin({
        pos_customer: customer.name,
        email: storeLoginEmail.trim() || customer.email || '',
        password: storeLoginPassword,
        full_name: customer.customer_name,
        mobile: customer.mobile || '',
        approve: 1,
      });
      setStoreLoginPassword('');
      toast.success('Store login saved — customer can sign in to the account portal');
      await storeLoginQ.refetch();
    } catch (err: any) {
      toast.error(getApiErrorMessage(err, 'Could not save store login'));
    } finally {
      setStoreLoginBusy(false);
    }
  };

  const disableStoreLogin = async () => {
    if (!customer) return;
    setStoreLoginBusy(true);
    try {
      await storefrontAdminApi.disableStoreCustomerLogin({ pos_customer: customer.name });
      toast.success('Store login disabled');
      await storeLoginQ.refetch();
    } catch (err: any) {
      toast.error(getApiErrorMessage(err, 'Could not disable store login'));
    } finally {
      setStoreLoginBusy(false);
    }
  };
  const standing = data?.standing;
  const counts = data?.counts;

  const standingLabel = useMemo(() => {
    const outstanding = standing?.outstanding || 0;
    const advance = standing?.advance || 0;
    if (outstanding > 0) return { tone: 'owe' as const, text: t('customers.dashboard.owes', { amount: formatMoney(outstanding) }) };
    if (advance > 0) return { tone: 'advance' as const, text: t('customers.dashboard.hasAdvance', { amount: formatMoney(advance) }) };
    return { tone: 'settled' as const, text: t('customers.dashboard.settled') };
  }, [standing, t]);

  const tabs: { key: TabKey; label: string; count?: number }[] = useMemo(() => {
    const list: { key: TabKey; label: string; count?: number }[] = [
      { key: 'overview', label: t('customers.dashboard.tabOverview', 'Overview') },
      { key: 'invoices', label: t('customers.dashboard.tabInvoices') },
      { key: 'quotes', label: t('customers.dashboard.tabQuotes') },
      {
        key: 'prices',
        label: t('customers.specialPrices', 'Special Prices'),
        count: customer?.custom_prices?.length || 0,
      },
      { key: 'collection', label: t('customers.dashboard.tabCollection') },
    ];
    if (posSettings?.enable_delivery) {
      list.push({ key: 'delivery', label: t('customers.dashboard.tabDelivery') });
    }
    list.push({ key: 'ledger', label: t('customers.dashboard.tabLedger') });
    return list;
  }, [posSettings?.enable_delivery, customer?.custom_prices?.length, t]);

  const handleDeleteCustomPriceConfirm = async () => {
    if (!deletePriceTarget || !customer) return;
    try {
      setDeletePricePending(true);
      const updatedPrices = (customer.custom_prices || []).filter(
        (_: any, idx: number) => idx !== deletePriceTarget.index
      );
      await createCustomerRouted({
        name: customer.name,
        customer_name: customer.customer_name,
        custom_prices: updatedPrices,
        _action: 'update',
      });
      qc.invalidateQueries({ queryKey: ['customer-dashboard', customerId] });
      qc.invalidateQueries({ queryKey: ['customers'] });
      toast.success('Special price removed successfully');
      setDeletePriceTarget(null);
    } catch (err: any) {
      toast.error(err.message || 'Failed to remove custom price');
    } finally {
      setDeletePricePending(false);
    }
  };

  const fulfillmentBadge = (type?: string) => {
    if (type === 'Delivery') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-violet-50 text-violet-700 border border-violet-200">
          <Truck className="w-3 h-3" />
          {t('customers.dashboard.delivery')}
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-sky-50 text-sky-700 border border-sky-200">
        <Package className="w-3 h-3" />
        {t('customers.dashboard.collection')}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 flex">
      <Sidebar />
      <div className="flex-1 flex flex-col min-w-0">
        <TrialBanner />
        <div className="p-4 sm:p-6 max-w-7xl w-full mx-auto">
          <div className="flex items-center gap-3 mb-4">
            <button
              onClick={toggleSidebar}
              className="lg:hidden p-2 -ml-2 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg shrink-0"
            >
              <Menu className="w-5 h-5" />
            </button>
            <button
              type="button"
              onClick={() => navigate('/customers')}
              className="inline-flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900"
            >
              <ArrowLeft className="w-4 h-4" />
              {t('customers.title')}
            </button>
          </div>

          {offline ? (
            <div className="card p-8 text-center text-gray-500">
              <Users className="w-10 h-10 mx-auto mb-2 opacity-30" />
              <p className="font-medium">{t('customers.dashboard.onlineRequired')}</p>
              <Link to="/customers" className="text-primary-700 text-sm mt-2 inline-block">
                {t('customers.title')}
              </Link>
            </div>
          ) : isLoading ? (
            <div className="space-y-4">
              <div className="card p-6 h-28 animate-pulse bg-white" />
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="card p-4 h-20 animate-pulse" />
                ))}
              </div>
            </div>
          ) : isError || !customer ? (
            <div className="card p-8 text-center text-gray-500">
              <p>{t('customers.dashboard.notFound')}</p>
              <Link to="/customers" className="text-primary-700 text-sm mt-2 inline-block">
                {t('customers.title')}
              </Link>
            </div>
          ) : (
            <>
              <div className="space-y-4 mb-4">
                <CustomerDetailHero
                  customer={customer}
                  standingLabel={standingLabel}
                  lastPurchaseDate={data?.last_purchase_date}
                  canEdit={!isCashierLevel(role)}
                  canDelete={Boolean(isAdmin && !customer.is_walk_in && !customer.is_default)}
                  onAddPayment={() => setShowAddPayment(true)}
                  onEdit={() => {
                    setEditModalTab('basic');
                    setEditModalPriceIndex(null);
                    setShowEditModal(true);
                    setHeaderMoreOpen(false);
                  }}
                  onDelete={() => {
                    setShowDeleteCustomerConfirm(true);
                    setHeaderMoreOpen(false);
                  }}
                  onLedger={() => {
                    setTab('ledger');
                    setHeaderMoreOpen(false);
                  }}
                  moreOpen={headerMoreOpen}
                  onToggleMore={() => setHeaderMoreOpen((v) => !v)}
                />

                <CustomerKpiCards
                  totalSales={standing?.total_sale || 0}
                  orderCount={counts?.invoices || standing?.invoice_count || 0}
                  outstanding={standing?.outstanding || 0}
                  advance={standing?.advance || 0}
                  returnsTotal={standing?.returns_total || 0}
                  loyaltyPoints={Number(loyaltyInfo?.balance) || Number(customer.loyalty_points_balance) || 0}
                  loyaltyValue={Number(loyaltyInfo?.value) || 0}
                />

                <CustomerMidCards
                  customer={customer}
                  shopIsUk={shopIsUk}
                  loyaltyInfo={loyaltyInfo}
                  loyaltyQrDataUrl={loyaltyQrDataUrl}
                  enrolMarketing={enrolMarketing}
                  onEnrolMarketingChange={setEnrolMarketing}
                  enrolBusy={enrolBusy}
                  onEnrol={async () => {
                    setEnrolBusy(true);
                    try {
                      await loyaltyApi.enrolCustomer({
                        customer: customerId,
                        accept_terms: true,
                        marketing_consent: enrolMarketing,
                      });
                      toast.success('Customer enrolled in loyalty');
                      void refetchLoyalty();
                      void qc.invalidateQueries({ queryKey: ['customer-dashboard', customerId] });
                    } catch (err) {
                      toast.error(getApiErrorMessage(err, 'Enrol failed'));
                    } finally {
                      setEnrolBusy(false);
                    }
                  }}
                  onShowQr={() => setShowLoyaltyQr(true)}
                  adjustPts={adjustPts}
                  adjustReason={adjustReason}
                  onAdjustPtsChange={setAdjustPts}
                  onAdjustReasonChange={setAdjustReason}
                  adjustBusy={adjustBusy}
                  onAdjust={async () => {
                    const pts = parseFloat(adjustPts);
                    if (!pts || !adjustReason.trim()) {
                      toast.error('Enter points and a reason');
                      return;
                    }
                    setAdjustBusy(true);
                    try {
                      await loyaltyApi.adjustPoints({
                        customer: customerId,
                        points: pts,
                        reason: adjustReason.trim(),
                      });
                      toast.success('Points adjusted');
                      setAdjustPts('');
                      setAdjustReason('');
                      void refetchLoyalty();
                      void qc.invalidateQueries({ queryKey: ['customer-dashboard', customerId] });
                    } catch (err) {
                      toast.error(getApiErrorMessage(err, 'Adjust failed'));
                    } finally {
                      setAdjustBusy(false);
                    }
                  }}
                  standing={standing}
                  isAdmin={isAdmin}
                  storeLogin={storeLoginQ.data as any}
                  storeLoginLoading={storeLoginQ.isLoading}
                  storeLoginOpen={storeLoginOpen}
                  onToggleStoreLogin={() => setStoreLoginOpen((v) => !v)}
                  storeLoginEmail={storeLoginEmail}
                  storeLoginPassword={storeLoginPassword}
                  onStoreLoginEmail={setStoreLoginEmail}
                  onStoreLoginPassword={setStoreLoginPassword}
                  storeLoginBusy={storeLoginBusy}
                  onSaveStoreLogin={() => void saveStoreLogin()}
                  onDisableStoreLogin={() => void disableStoreLogin()}
                />
              </div>

              {/* Tabs */}
              <div className="flex gap-1 overflow-x-auto mb-4 border-b border-gray-200 pb-px">
                {tabs.map((tab) => (
                  <button
                    key={tab.key}
                    type="button"
                    onClick={() => setTab(tab.key)}
                    className={`px-3 py-2 text-sm font-medium whitespace-nowrap border-b-2 -mb-px transition-colors flex items-center gap-1.5 ${
                      activeTab === tab.key
                        ? 'border-primary-600 text-primary-800 font-semibold'
                        : 'border-transparent text-gray-500 hover:text-gray-800'
                    }`}
                  >
                    <span>{tab.label}</span>
                    {tab.count !== undefined && tab.count > 0 && (
                      <span
                        className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold ${
                          activeTab === tab.key
                            ? 'bg-primary-100 text-primary-800'
                            : 'bg-gray-100 text-gray-600'
                        }`}
                      >
                        {tab.count}
                      </span>
                    )}
                  </button>
                ))}
              </div>

              {activeTab === 'overview' && (
                <CustomerOverviewPanel
                  purchaseTrend={data?.purchase_trend}
                  topCategories={data?.top_categories}
                  insights={data?.insights}
                  recentTransactions={data?.recent_transactions}
                  onOpenVoucher={(voucherNo, txnType) => {
                    if (txnType === 'Sale') openInvoiceReceipt(voucherNo);
                    else if (txnType === 'Return') setTab('invoices');
                    else setTab('ledger');
                  }}
                />
              )}

              {activeTab === 'invoices' && (
                <div className="space-y-3">
                  <div className="flex flex-wrap gap-2">
                    {['All', 'Unpaid', 'Partial', 'Paid', 'Returned', ...(canViewVoided ? ['Voided'] : [])].map((s) => (
                      <button
                        key={s}
                        type="button"
                        onClick={() => setInvoiceStatusFilter(s)}
                        className={`px-2.5 py-1.5 rounded-lg text-xs font-medium border ${
                          invoiceStatusFilter === s
                            ? 'bg-primary-50 border-primary-300 text-primary-800'
                            : 'bg-white border-gray-200 text-gray-600'
                        }`}
                      >
                        {s === 'All'
                          ? t('customers.dashboard.filterAll')
                          : s === 'Unpaid'
                            ? t('customers.dashboard.unpaid')
                            : s === 'Partial'
                              ? t('customers.dashboard.partial')
                              : s === 'Paid'
                                ? t('customers.dashboard.paid')
                                : s === 'Returned'
                                  ? t('customers.dashboard.returned', 'Returned')
                                  : t('customers.dashboard.voided', 'Voided')}
                      </button>
                    ))}
                    <button
                      type="button"
                      className="ml-auto text-xs text-primary-700"
                      onClick={() => setShowInvoiceLedger(true)}
                    >
                      {t('customers.dashboard.openInvoiceLedger')}
                    </button>
                  </div>
                  {invoicesLoading ? (
                    <div className="card p-8 text-center text-gray-400">{t('customers.loading')}</div>
                  ) : invoices.length === 0 ? (
                    <div className="card p-8 text-center text-gray-400">
                      {t('customers.dashboard.noInvoices')}
                    </div>
                  ) : (
                    <div className="card overflow-x-auto">
                      <table className="min-w-full text-sm">
                        <thead className="bg-gray-50 text-left text-xs text-gray-500">
                          <tr>
                            <th className="px-3 py-2">{t('customers.dashboard.invoice')}</th>
                            <th className="px-3 py-2">{t('customers.dashboard.date')}</th>
                            <th className="px-3 py-2">{t('customers.dashboard.type')}</th>
                            <th className="px-3 py-2">{t('common.status')}</th>
                            <th className="px-3 py-2 text-right">{t('customers.dashboard.amount')}</th>
                            <th className="px-3 py-2 text-right">{t('customers.outstanding')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {invoices.map((inv: any) => (
                            <tr key={inv.name} className="border-t border-gray-100">
                              <td className="px-3 py-2">
                                <button
                                  type="button"
                                  className="font-medium text-primary-700 hover:underline"
                                  onClick={() => openInvoiceReceipt(inv.name)}
                                >
                                  {inv.name}
                                </button>
                              </td>
                              <td className="px-3 py-2 text-gray-600">
                                {inv.invoice_date
                                  ? format(new Date(inv.invoice_date), 'dd MMM yyyy')
                                  : '—'}
                              </td>
                              <td className="px-3 py-2">
                                {fulfillmentBadge(inv.fulfillment_type || 'Pickup')}
                              </td>
                              <td className="px-3 py-2">
                                <span
                                  className={
                                    inv.status === 'Cancelled'
                                      ? 'text-xs font-medium text-red-700 bg-red-50 border border-red-100 px-2 py-0.5 rounded-full'
                                      : inv.status === 'Returned' || inv.status === 'Partially Returned'
                                        ? 'text-xs font-medium text-amber-800 bg-amber-50 border border-amber-100 px-2 py-0.5 rounded-full'
                                        : inv.status === 'Paid'
                                          ? 'text-xs font-medium text-emerald-800 bg-emerald-50 border border-emerald-100 px-2 py-0.5 rounded-full'
                                          : 'text-gray-600'
                                  }
                                >
                                  {inv.status}
                                </span>
                              </td>
                              <td className="px-3 py-2 text-right font-medium">
                                {formatMoney(inv.net_total || 0)}
                              </td>
                              <td className="px-3 py-2 text-right">
                                {formatMoney(inv.balance_amount || 0)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'quotes' && (
                <div>
                  {quotesLoading ? (
                    <div className="card p-8 text-center text-gray-400">{t('customers.loading')}</div>
                  ) : !(quotesData?.items || []).length ? (
                    <div className="card p-8 text-center text-gray-400">
                      {t('customers.dashboard.noQuotes')}
                    </div>
                  ) : (
                    <div className="card overflow-x-auto">
                      <table className="min-w-full text-sm">
                        <thead className="bg-gray-50 text-left text-xs text-gray-500">
                          <tr>
                            <th className="px-3 py-2">{t('customers.dashboard.quote')}</th>
                            <th className="px-3 py-2">{t('customers.dashboard.date')}</th>
                            <th className="px-3 py-2">{t('common.status')}</th>
                            <th className="px-3 py-2 text-right">{t('customers.dashboard.amount')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(quotesData?.items || []).map((q: any) => (
                            <tr key={q.name} className="border-t border-gray-100">
                              <td className="px-3 py-2 font-medium">{q.name}</td>
                              <td className="px-3 py-2 text-gray-600">
                                {q.expected_date || q.valid_till || q.invoice_date || q.date
                                  ? formatCalendarDate(q.expected_date || q.valid_till || q.invoice_date || q.date)
                                  : '—'}
                              </td>
                              <td className="px-3 py-2">{q.status}</td>
                              <td className="px-3 py-2 text-right font-medium">
                                {formatMoney(q.total ?? q.net_total ?? q.grand_total ?? 0)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  <button
                    type="button"
                    className="mt-3 text-sm text-primary-700 hover:text-primary-800 font-medium"
                    onClick={() => {
                      const query = customer?.customer_name || customer?.name || '';
                      navigate(`/sales?tab=quotes&search=${encodeURIComponent(query)}&status=All`);
                    }}
                  >
                    {t('customers.dashboard.openQuotesModal')}
                  </button>
                </div>
              )}

              {activeTab === 'prices' && (
                <div className="space-y-4">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-white p-4 rounded-xl border border-gray-200">
                    <div>
                      <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2">
                        <Tag className="w-4 h-4 text-primary-600" />
                        Special Product Prices
                        {customer?.custom_prices?.length ? (
                          <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-primary-50 text-primary-700 border border-primary-200">
                            {customer.custom_prices.length} {customer.custom_prices.length === 1 ? 'Rule' : 'Rules'}
                          </span>
                        ) : null}
                      </h2>
                      <p className="text-xs text-gray-500 mt-0.5">
                        Agreed rates that take precedence over standard catalog pricing at checkout.
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      {!isCashierLevel(role) && (
                        <button
                          type="button"
                          onClick={() => {
                            setEditModalTab('prices');
                            setEditModalPriceIndex(null);
                            setShowEditModal(true);
                          }}
                          className="btn-primary inline-flex items-center gap-1.5 text-xs py-2 px-3 whitespace-nowrap"
                        >
                          <Plus className="w-4 h-4" />
                          Add Custom Price
                        </button>
                      )}
                    </div>
                  </div>

                  {(!customer?.custom_prices || customer.custom_prices.length === 0) ? (
                    <div className="card p-10 text-center text-gray-500">
                      <Tag className="w-10 h-10 mx-auto mb-3 opacity-30 text-gray-400" />
                      <h3 className="text-sm font-semibold text-gray-900 mb-1">
                        No Special Prices Configured
                      </h3>
                      <p className="text-xs text-gray-500 max-w-md mx-auto mb-4">
                        This customer currently uses standard retail pricing for all catalog items. Add agreed prices to provide personalized discounts or fixed rates.
                      </p>
                      {!isCashierLevel(role) && (
                        <button
                          type="button"
                          onClick={() => {
                            setEditModalTab('prices');
                            setEditModalPriceIndex(null);
                            setShowEditModal(true);
                          }}
                          className="btn-primary inline-flex items-center gap-1.5 text-xs py-2 px-3.5 mx-auto"
                        >
                          <Plus className="w-4 h-4" />
                          Add Custom Price
                        </button>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {customer.custom_prices.length > 5 && (
                        <div className="relative max-w-sm">
                          <Search className="w-3.5 h-3.5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                          <input
                            type="text"
                            placeholder="Search special price items…"
                            value={priceSearchQuery}
                            onChange={(e) => setPriceSearchQuery(e.target.value)}
                            className="input-field text-xs pl-8 py-1.5"
                          />
                        </div>
                      )}

                      <div className="card overflow-x-auto shadow-xs border border-gray-200">
                        <table className="min-w-full text-xs">
                          <thead className="bg-gray-50 text-left text-[11px] uppercase tracking-wider text-gray-500 border-b border-gray-100">
                            <tr>
                              <th className="px-4 py-3">Product</th>
                              <th className="px-4 py-3 text-right">Standard Rate</th>
                              <th className="px-4 py-3 text-right text-emerald-700">Custom Agreed Rate</th>
                              {posSettings?.enable_delivery && (
                                <th className="px-4 py-3 text-right text-blue-700">Delivery Rate</th>
                              )}
                              {!isCashierLevel(role) && (
                                <th className="px-4 py-3 text-center w-24">Actions</th>
                              )}
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100">
                            {(customer.custom_prices as CustomerItemPrice[])
                              .map((row: CustomerItemPrice, originalIdx: number) => ({ row, originalIdx }))
                              .filter((entry: { row: CustomerItemPrice; originalIdx: number }) => {
                                const row = entry.row;
                                if (!priceSearchQuery.trim()) return true;
                                const q = priceSearchQuery.toLowerCase();
                                return (
                                  (row.item_name || '').toLowerCase().includes(q) ||
                                  (row.item || '').toLowerCase().includes(q) ||
                                  (row.variant_name || '').toLowerCase().includes(q)
                                );
                              })
                              .map((entry: { row: CustomerItemPrice; originalIdx: number }) => {
                                const row = entry.row;
                                const originalIdx = entry.originalIdx;
                                const discount =
                                  row.standard_price && row.standard_price > row.custom_price
                                    ? Math.round(
                                        ((row.standard_price - row.custom_price) / row.standard_price) * 100
                                      )
                                    : null;
                                return (
                                  <tr key={`${row.item}_${row.variant || ''}_${originalIdx}`} className="hover:bg-gray-50/80 transition-colors">
                                    <td className="px-4 py-3">
                                      <div className="font-semibold text-gray-900 text-sm">
                                        {row.item_name || row.item}
                                      </div>
                                      <div className="flex items-center gap-2 mt-0.5 text-gray-500 font-mono text-[11px]">
                                        <span>{row.item}</span>
                                        {row.variant_name && (
                                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-purple-50 text-purple-700 border border-purple-200">
                                            {row.variant_name}
                                          </span>
                                        )}
                                      </div>
                                    </td>
                                    <td className="px-4 py-3 text-right font-mono text-gray-500 text-xs">
                                      {row.standard_price != null ? formatMoney(row.standard_price) : '—'}
                                    </td>
                                    <td className="px-4 py-3 text-right">
                                      <div className="inline-flex items-center justify-end gap-1.5">
                                        <span className="font-mono font-bold text-emerald-700 text-sm">
                                          {formatMoney(row.custom_price)}
                                        </span>
                                        {discount !== null && discount > 0 && (
                                          <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
                                            -{discount}%
                                          </span>
                                        )}
                                      </div>
                                    </td>
                                    {posSettings?.enable_delivery && (
                                      <td className="px-4 py-3 text-right font-mono text-blue-700 text-xs">
                                        {row.custom_delivery_price != null && row.custom_delivery_price > 0
                                          ? formatMoney(row.custom_delivery_price)
                                          : '—'}
                                      </td>
                                    )}
                                    {!isCashierLevel(role) && (
                                      <td className="px-4 py-3 text-center">
                                        <div className="flex items-center justify-center gap-1.5">
                                          <button
                                            type="button"
                                            onClick={() => {
                                              setEditModalTab('prices');
                                              setEditModalPriceIndex(originalIdx);
                                              setShowEditModal(true);
                                            }}
                                            className="p-1.5 text-gray-500 hover:text-primary-700 hover:bg-primary-50 rounded-lg transition-colors"
                                            title="Edit Price Rule"
                                          >
                                            <Pencil className="w-3.5 h-3.5" />
                                          </button>
                                          <button
                                            type="button"
                                            onClick={() => {
                                              setDeletePriceTarget({
                                                item: row.item,
                                                item_name: row.item_name || row.item,
                                                variant: row.variant_name,
                                                index: originalIdx,
                                              });
                                            }}
                                            className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                            title="Remove Price Rule"
                                          >
                                            <Trash2 className="w-3.5 h-3.5" />
                                          </button>
                                        </div>
                                      </td>
                                    )}
                                  </tr>
                                );
                              })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'collection' && (
                <CustomerFulfillmentTab
                  customerId={customerId}
                  mode="Pickup"
                  onOpenInvoice={openInvoiceReceipt}
                />
              )}

              {activeTab === 'delivery' && (
                <CustomerFulfillmentTab
                  customerId={customerId}
                  mode="Delivery"
                  onOpenInvoice={openInvoiceReceipt}
                />
              )}

              {activeTab === 'ledger' && customer && (
                <PartyLedgerModal
                  isOpen
                  variant="inline"
                  partyType="POS Customer"
                  party={customer.name}
                  partyName={customer.customer_name}
                />
              )}
            </>
          )}
        </div>
      </div>

      {customer && (
        <>
          <SaleInvoiceLedgerModal
            isOpen={showInvoiceLedger}
            onClose={() => setShowInvoiceLedger(false)}
            customer={customer.name}
            customerName={customer.customer_name}
          />
          <CustomerQuotationsModal
            isOpen={showQuotations}
            onClose={() => setShowQuotations(false)}
            customerId={customer.name}
            customerName={customer.customer_name}
          />
          <AddCustomerPaymentModal
            isOpen={showAddPayment}
            onClose={() => {
              setShowAddPayment(false);
              qc.invalidateQueries({ queryKey: ['customer-dashboard', customerId] });
              qc.invalidateQueries({ queryKey: ['customers'] });
            }}
            customer={{
              name: customer.name,
              customer_name: customer.customer_name,
              outstanding_balance: standing?.outstanding || 0,
            }}
          />
          <CustomerFormModal
            isOpen={showEditModal}
            onClose={() => {
              setShowEditModal(false);
              setEditModalPriceIndex(null);
            }}
            customer={
              {
                ...customer,
                loyalty_enrolled:
                  Number(customer.loyalty_enrolled) || (loyaltyInfo?.enrolled ? 1 : 0),
                marketing_consent: Number(customer.marketing_consent) || 0,
              } as POSCustomer
            }
            initialTab={editModalTab}
            initialEditPriceIndex={editModalPriceIndex}
            onSaved={() => {
              qc.invalidateQueries({ queryKey: ['customer-dashboard', customerId] });
              qc.invalidateQueries({ queryKey: ['customers'] });
              qc.invalidateQueries({ queryKey: ['customer-loyalty', customerId] });
              qc.invalidateQueries({ queryKey: ['customer-form-loyalty', customerId] });
            }}
            onDeleted={() => navigate('/customers')}
          />
          {showLoyaltyQr && (
            <LoyaltyCardModal
              open={showLoyaltyQr}
              onClose={() => setShowLoyaltyQr(false)}
              design={loyaltyInfo?.card_design || 'Fresh'}
              colour={loyaltyInfo?.card_colour || undefined}
              logoUrl={resolveFileUrl(loyaltyInfo?.card_logo || '') || null}
              shopName={loyaltyInfo?.shop_name || ''}
              tagline={loyaltyInfo?.card_tagline || ''}
              programName={loyaltyInfo?.program_name || 'Loyalty Rewards'}
              customerName={customer.customer_name}
              memberId={loyaltyInfo?.barcode || customer.name}
              points={Number(loyaltyInfo?.balance) || 0}
              value={Number(loyaltyInfo?.value) || 0}
              tier={
                loyaltyInfo?.tier?.tier_name
                  ? {
                      tier_name: loyaltyInfo.tier.tier_name,
                      badge_color: loyaltyInfo.tier.badge_color,
                    }
                  : null
              }
              showPoints={loyaltyInfo?.card_show_points !== false}
              showValue={loyaltyInfo?.card_show_value !== false}
              showTier={loyaltyInfo?.card_show_tier !== false}
              qrDataUrl={loyaltyQrDataUrl}
            />
          )}
          {showDeleteCustomerConfirm && customer && (
            <ConfirmModal
              title={t('confirmDelete.title', 'Confirm Delete')}
              message={t('confirmDelete.message', {
                label: customer.customer_name,
                defaultValue: `Are you sure you want to delete "${customer.customer_name}"? This action is permanent and cannot be undone.`,
              })}
              confirmText={t('common.delete', 'Delete')}
              cancelText={t('common.cancel', 'Cancel')}
              isPending={deleteCustomerPending}
              onConfirm={handleDeleteCustomerConfirm}
              onClose={() => {
                if (!deleteCustomerPending) {
                  setShowDeleteCustomerConfirm(false);
                }
              }}
              type="danger"
            />
          )}
          {deleteCustomerError && (
            <ConfirmModal
              title={t('confirmDelete.cannotDelete', 'Cannot Delete')}
              message={deleteCustomerError}
              confirmText={t('confirmDelete.ok', 'OK')}
              hideCancel
              onConfirm={() => setDeleteCustomerError(null)}
              onClose={() => setDeleteCustomerError(null)}
              type="warning"
            />
          )}
          {deletePriceTarget && (
            <ConfirmModal
              title="Remove Custom Price"
              message={`Are you sure you want to remove the special price rule for "${deletePriceTarget.item_name || deletePriceTarget.item}"?`}
              confirmText="Remove Price"
              isPending={deletePricePending}
              onConfirm={handleDeleteCustomPriceConfirm}
              onClose={() => setDeletePriceTarget(null)}
              type="danger"
            />
          )}
          {receiptInvoiceName && (
            <Receipt
              invoiceName={receiptInvoiceName}
              onClose={() => setReceiptInvoiceName(null)}
            />
          )}
        </>
      )}
    </div>
  );
}
